import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-async';
import { EntityHistoryList, EntityHistoryCard } from '@koob/entity-history';
import KoobTitle from 'components/Koob/KoobTitle';
import KoobPlaceholder from 'components/Koob/KoobPlaceholder';
import { KoobCard, KoobPagination } from '@koob/ui';
import { getEntityHistory } from 'api/node/entityHistory';
import ExperienceHeader from '../Form/Partials/ExperienceHeader';
import ExperienceLayout from '../Form/Partials/ExperienceLayout';
import { getExperience } from 'api/node/experiences';

const DEFAULT_PER_PAGE = 20;

export default function ExperienceHistoryList() {
  const { t } = useTranslation('experiences');
  const { experienceId } = useParams();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { data: experienceData, isLoading: isExperienceLoading } = useAsync({
    promiseFn: getExperience,
    experienceId: experienceId,
  });

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getEntityHistory({
        entityType: 'Experience',
        entityId: experienceId,
        page,
        perPage,
      });
      setData(response);
    } catch (error) {
      console.error('Failed to fetch experience history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [experienceId, page, perPage]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const items = data?.data?.items ?? [];

  const totalPages = useMemo(() => {
    const total = data?.data?.total;
    const limit = data?.data?.limit;
    if (total !== undefined && limit) {
      return Math.ceil(total / limit);
    }
    return 1;
  }, [data]);

  const fieldLabels = {
    name: t('history.fields.name', 'Name'),
    state: t('history.fields.state', 'State'),
    description: t('history.fields.description', 'Description'),
    shortDescription: t('history.fields.shortDescription', 'Short Description'),
    durationDays: t('history.fields.durationDays', 'Duration (Days)'),
    durationHours: t('history.fields.durationHours', 'Duration (Hours)'),
    durationMinutes: t('history.fields.durationMinutes', 'Duration (Minutes)'),
    type: t('history.fields.type', 'Type'),
    pricingType: t('history.fields.pricingType', 'Pricing Type'),
    translations: t('history.fields.translations', 'Translations'),
    periodsExperience: t('history.fields.periodsExperience', 'Periods'),
    mediaLinksExperience: t(
      'history.fields.mediaLinksExperience',
      'Media Links',
    ),
    extras: t('history.fields.extras', 'Extras'),
    programs: t('history.fields.programs', 'Programs'),
    themes: t('history.fields.themes', 'Themes'),
    segments: t('history.fields.segments', 'Segments'),
    isRepeatable: t('history.fields.isRepeatable', 'Repeatable'),
    isTransitionalProgram: t(
      'history.fields.isTransitionalProgram',
      'Transitional Program',
    ),
    guidingLanguages: t('history.fields.guidingLanguages', 'Guiding Languages'),
    experienceExperienceSegments: t('history.fields.segments', 'Segments'),
    experienceExperienceThemes: t('history.fields.themes', 'Themes'),
    accreditationExperience: t(
      'history.fields.accreditations',
      'Accreditations',
    ),
    inCityId: t('history.fields.inCityId', 'City'),
    startPositionId: t('history.fields.startPositionId', 'Start Position'),
    endPositionId: t('history.fields.endPositionId', 'End Position'),
    accomodationHotelId: t(
      'history.fields.accomodationHotelId',
      'Accommodation Hotel',
    ),
    accomodationExperienceId: t(
      'history.fields.accomodationExperienceId',
      'Accommodation Experience',
    ),
    transfers: t('history.fields.transfers', 'Transfers'),
    extrasWithDependencies: t(
      'history.fields.extrasWithDependencies',
      'Extras with Dependencies',
    ),
    includedOptions: t(
      'history.fields.includedOptions',
      'Included Additional services',
    ),
    suggestedOptions: t(
      'history.fields.suggestedOptions',
      'Suggested Additional services',
    ),
    programIncludedOptions: t(
      'history.fields.programIncludedOptions',
      'Included Additional services',
    ),
    programSuggestedOptions: t(
      'history.fields.programSuggestedOptions',
      'Suggested Additional services',
    ),
    alternativeHotels: t(
      'history.fields.alternativeHotels',
      'Alternative Hotels',
    ),
    programExperiences: t('history.fields.programExperiences', 'Experiences'),
    pois: t('history.fields.pois', 'Points of Interest'),
  };

  const actionLabels = {
    create: t('history.actions.create', 'Created'),
    update: t('history.actions.update', 'Updated'),
    delete: t('history.actions.delete', 'Deleted'),
  };

  return (
    <ExperienceLayout>
      {isExperienceLoading ? (
        <KoobPlaceholder className="h-32 w-full rounded-lg mb-5" />
      ) : (
        <ExperienceHeader values={experienceData?.data} />
      )}

      {isLoading ? (
        <KoobCard>
          <div className="mb-4">
            <KoobPlaceholder className="h-6 w-48 rounded mb-2" />
            <KoobPlaceholder className="h-4 w-72 rounded" />
          </div>
          <div className="space-y-4">
            <KoobPlaceholder className="h-24 w-full rounded-lg" />
            <KoobPlaceholder className="h-24 w-full rounded-lg" />
            <KoobPlaceholder className="h-24 w-full rounded-lg" />
          </div>
        </KoobCard>
      ) : (
        <KoobCard>
          <div className="mb-4">
            <KoobTitle>{t('history.title', 'Edit History')}</KoobTitle>
            <p className="text-gray-500">{t('history.subtitle')}</p>
          </div>

          <div className="min-h-[400px]">
            <EntityHistoryList
              items={items}
              isLoading={false}
              emptyMessage={t('history.empty', 'No edit history available')}
              fieldLabels={fieldLabels}
              actionLabels={actionLabels}
              renderCard={(history, index) => (
                <EntityHistoryCard
                  key={history.id || index}
                  history={history}
                  fieldLabels={fieldLabels}
                  actionLabelsOverride={actionLabels}
                />
              )}
            />
          </div>

          {items.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <KoobPagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                totalCount={Number(data?.data?.total)}
                perPage={perPage}
                setPerPage={(newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                }}
              />
            </div>
          )}
        </KoobCard>
      )}
    </ExperienceLayout>
  );
}
